import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {computeTrendLayout, type TrendLayout} from '../utils/trendCardLayout';

export interface MeasurableTrend {
    name: string;
    value: string;
}

/** Widest child, rounded up so a subpixel remainder can never clip a glyph. */
const widestChild = (container: HTMLElement | null): number => {
    if (!container) return 0;
    return Array.from(container.children).reduce(
        (max, child) => Math.max(max, Math.ceil(child.getBoundingClientRect().width)),
        0
    );
};

/**
 * Measures the carousel viewport and the text that has to fit inside its cards,
 * then hands both to computeTrendLayout.
 *
 * Text is measured by rendering the real strings in the real classes off-screen
 * rather than by reconstructing a font string for canvas measureText — that
 * keeps letter-spacing and font fallback faithful, and it means a Super Admin
 * assay rename is picked up automatically with no code change.
 *
 * The caller must render `probe` and attach `viewportRef` to the
 * overflow-hidden strip.
 */
export function useTrendCardLayout(trends: MeasurableTrend[]): {
    viewportRef: React.RefObject<HTMLDivElement>;
    probe: React.ReactNode;
    layout: TrendLayout | null;
} {
    const viewportRef = useRef<HTMLDivElement>(null);
    const nameProbeRef = useRef<HTMLDivElement>(null);
    const valueProbeRef = useRef<HTMLDivElement>(null);
    const [layout, setLayout] = useState<TrendLayout | null>(null);

    // Array identity changes on every render; the rendered strings are what
    // actually matter. Keying the effects on this instead of on `trends` is what
    // stops the measure effect from re-running forever. The separators are
    // characters that cannot occur in a biomarker name or a lab value, so two
    // different trend lists can never collide on the same key.
    const trendsKey = trends.map((trend) => `${trend.name}\u0000${trend.value}`).join('\u001f');

    const recompute = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const next = computeTrendLayout({
            containerWidth: viewport.getBoundingClientRect().width,
            maxValueWidth: widestChild(valueProbeRef.current),
            maxNameWidth: widestChild(nameProbeRef.current),
        });

        // Bail on an unchanged result: ResizeObserver fires on every animation
        // frame during a drag-resize, and a fresh object each time would
        // re-render the whole carousel for nothing.
        setLayout((prev) => {
            if (prev === next) return prev;
            if (!prev || !next) return next;
            return prev.cardWidth === next.cardWidth && prev.cardsPerView === next.cardsPerView ? prev : next;
        });
    }, []);

    // Measure after the probe has been laid out, and again whenever the
    // rendered strings change (category switch, data load, assay rename).
    useLayoutEffect(() => {
        recompute();
    }, [recompute, trendsKey]);

    // Container width changes: window resize, sidebar toggle, browser zoom.
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(() => recompute());
        observer.observe(viewport);
        return () => observer.disconnect();
    }, [recompute]);

    // Poppins loads async. Measuring before it resolves bakes in fallback
    // metrics and yields cards a few px too narrow.
    useEffect(() => {
        const fonts = typeof document === 'undefined' ? undefined : document.fonts;
        if (!fonts?.ready) return;

        let cancelled = false;
        fonts.ready.then(() => {
            if (!cancelled) recompute();
        });
        return () => {
            cancelled = true;
        };
    }, [recompute]);

    // Off-screen but still laid out — display:none would measure 0. Classes
    // mirror TrendCard's value and name spans exactly.
    const probe = useMemo(
        () => (
            <div aria-hidden className="pointer-events-none absolute -left-[9999px] top-0 h-0 overflow-hidden">
                <div ref={nameProbeRef}>
                    {trends.map((trend, index) => (
                        <span key={index} className="inline-block whitespace-nowrap text-xs font-medium">
                            {trend.name}
                        </span>
                    ))}
                </div>
                <div ref={valueProbeRef}>
                    {trends.map((trend, index) => (
                        <span key={index} className="inline-block whitespace-nowrap text-2xl font-normal leading-none">
                            {trend.value}
                        </span>
                    ))}
                </div>
            </div>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [trendsKey]
    );

    return {viewportRef, probe, layout};
}
